import { Component, type ErrorInfo, type ReactNode } from "react";

import { translate, type SupportedLocale } from "../i18n";
import { reportError } from "../lib/error-reporting";
import { resetFailedLoads } from "./preloadable";
import "./app-error-boundary.css";

// The app-wide error boundary (ADR-WBC1). Before it, a screen chunk that
// failed to load (a dropped connection, or a deploy that replaced the files
// the open page refers to) left #root empty: a blank page with nothing to do.
//
// Order of recovery:
//   1. the import itself is retried once in place (src/boot/preloadable.ts);
//   2. a chunk that still fails reloads the page once, so a stale page picks
//      up the current deploy. The navigation type tells a reload apart, so no
//      storage is needed to avoid a reload loop: a page that is already a
//      reload shows the message instead;
//   3. the message offers "Try again" (a fresh render with fresh loads,
//      src/boot/preloadable.ts resetFailedLoads) and "Reload page".
// Panels keep their own boundary (StitchErrorBoundary); this one catches what
// escapes to the root.

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|is not a valid JavaScript MIME type|ChunkLoadError/i;

/** True for the errors a missing or unreachable code-split file produces. */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  return CHUNK_ERROR.test(message);
}

/** True when the current page load was itself a reload. */
export function pageWasReloaded(win: Window = window): boolean {
  try {
    const [navigation] = win.performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    return navigation?.type === "reload";
  } catch {
    return false;
  }
}

type Props = {
  children: ReactNode;
  locale: SupportedLocale;
  /** Injected by tests; reloads the page. */
  reload?: () => void;
  /** Injected by tests; whether this page load was a reload. */
  wasReloaded?: () => boolean;
};

type State = { error: unknown; attempt: number };

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null, attempt: 0 };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error("[app] render failed", error);
    // ADR-WES1: Sentry is a lazy chunk, so the entry reports through the
    // buffering wrapper (never import the SDK here: the bundle budget forbids it).
    reportError(error, {
      tags: { boundary: "app", chunkLoad: String(isChunkLoadError(error)) },
      extra: { componentStack: info.componentStack }
    });
    const wasReloaded = this.props.wasReloaded ?? (() => pageWasReloaded());
    if (isChunkLoadError(error) && this.state.attempt === 0 && !wasReloaded()) {
      this.reload();
    }
  }

  private reload = () => {
    (this.props.reload ?? (() => window.location.reload()))();
  };

  private retry = () => {
    resetFailedLoads();
    this.setState((state) => ({ error: null, attempt: state.attempt + 1 }));
  };

  render() {
    if (this.state.error === null) {
      // The key remounts the tree on "Try again", so lazy screens load afresh.
      return <ErrorBoundaryReset key={this.state.attempt}>{this.props.children}</ErrorBoundaryReset>;
    }
    const t = (key: Parameters<typeof translate>[1]) => translate(this.props.locale, key);
    return (
      <section className="app-load-error" role="alert" data-testid="app-load-error">
        <h1>{t("boot.loadError.title")}</h1>
        <p>{t("boot.loadError.body")}</p>
        <div className="app-load-error-actions">
          <button className="app-load-error-primary" onClick={this.retry} type="button">
            {t("boot.loadError.retry")}
          </button>
          <button className="app-load-error-secondary" onClick={this.reload} type="button">
            {t("boot.loadError.reload")}
          </button>
        </div>
      </section>
    );
  }
}

function ErrorBoundaryReset({ children }: { children: ReactNode }) {
  return children;
}
